import { useCallback, useEffect, useState } from 'react';
import { GetHydratedCallRecordResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  CallRecordTagNotification,
  TagNotificationPayload,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { useCallRecordTagNotification } from '@frontend/notifications';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlert } from '@frontend/design-system';

export const useWebsocketCallRecordTagEventSubscription = () => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { selectedLocationIds } = useAppScopeStore();
  const [pendingViewParams, setPendingViewParams] = useState<CallRecordTagNotification | null>(null);

  const { open: openCallInsightPanel } = useCallInsightController({
    source: 'callRecordTagNotification',
  });

  const { locationFlagValueMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const callRecordQuery = useScopedQuery({
    queryKey: ['call-record-tag-event-details', pendingViewParams?.channelId, pendingViewParams?.locationId],
    queryFn: () =>
      pendingViewParams
        ? PhoneCallsApi.getCallRecord({
            callRecordId: pendingViewParams.callRecordId,
            channelId: pendingViewParams.channelId,
            locationId: pendingViewParams.locationId,
            startTime: pendingViewParams.startedAt,
            endTime: pendingViewParams.startedAt,
            includeCallRecording: true,
            hydrationFlags: {
              includeCallRecording: true,
              includePersonInfo: true,
              includePhoneTreeData: true,
              includeUserInfo: true,
              includeAiCallCreditsDetail: locationFlagValueMap[pendingViewParams.locationId] ?? false,
            },
          })
        : undefined,
    onError: () => {
      alert.error('Error fetching call record data');
    },
    enabled: !!pendingViewParams?.channelId,
  });

  useEffect(() => {
    const record = callRecordQuery.data?.record;

    if (record?.id && record.locationId) {
      openCallInsightPanel({
        callRecordId: record.id,
        locationId: record.locationId,
        includeCallIntelligence: true,
      });
    }
  }, [callRecordQuery.data?.record]);

  const { create: createNotification } = useCallRecordTagNotification({
    onView: async (event) => {
      const { payload: params } = event;
      if (pendingViewParams?.callRecordId === params.callRecordId) {
        callRecordQuery.refetch();
      } else {
        setPendingViewParams(params);
      }
    },
  });

  const handleCallRecordTagEventDetailsUpdate = ({
    channelId,
    locationId,
    tagsPayload,
  }: {
    channelId: string;
    locationId: string;
    tagsPayload: TagNotificationPayload[];
  }) => {
    const queryKeys = [selectedLocationIds, 'call-record-tag-event-details', channelId, locationId];
    const callRecord = queryClient.getQueryData(queryKeys) as GetHydratedCallRecordResponse;

    if (!callRecord?.record) {
      return;
    }

    const assignedTagIds = tagsPayload.map((tag) => tag.id);
    const updatedTagIds = callRecord.record.tagIds?.length
      ? Array.from(new Set([...callRecord.record.tagIds, ...assignedTagIds]))
      : assignedTagIds;

    queryClient.setQueryData(queryKeys, {
      record: {
        ...callRecord.record,
        tagIds: updatedTagIds,
      },
    });
  };

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.CALL_RECORD_TAG>>(
    (event) => {
      const { params } = event;

      // when view panel is already open, we need to update the call record data
      handleCallRecordTagEventDetailsUpdate({
        channelId: params.channelId,
        locationId: params.locationId,
        tagsPayload: params.tagsPayload,
      });

      createNotification({
        id: event.id,
        timestamp: Date.now(),
        type: 'call-record-tag',
        payload: params,
        state: {
          paused: false,
          status: 'unread',
        },
      });
    },
    [createNotification]
  );
  return useWebsocketEventSubscription(NotificationType.CALL_RECORD_TAG, eventHandler);
};
